import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, Trophy, Users, GraduationCap, Swords, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import AddToCalendarButton from "./AddToCalendarButton";

const KIND_META = {
  match:      { icon: Swords,         color: "text-[#00E676]", bg: "bg-[#00E676]/10",  label: "Match",      route: "matches" },
  tournament: { icon: Trophy,         color: "text-[#FFD60A]", bg: "bg-[#FFD60A]/10",  label: "Tournament", route: "tournaments" },
  league:     { icon: Users,          color: "text-[#007AFF]", bg: "bg-[#007AFF]/10",  label: "League",     route: "leagues" },
  lesson:     { icon: GraduationCap,  color: "text-[#BF5AF2]", bg: "bg-[#BF5AF2]/10",  label: "Lesson",     route: "lessons" },
};

const _fmtDay = (iso) => {
  if (!iso) return "TBA";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
};
const _fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};

/**
 * Club event calendar — flattened upcoming timeline of every match/tournament/
 * league/lesson attached to the club, grouped by day. Helps club admins spot
 * conflicts and identify empty weeks. Each row offers an "Add to calendar"
 * button so members can subscribe to events from a printed/shared schedule.
 */
export default function ClubEventCalendar({ clubs = [] }) {
  const [selectedClubId, setSelectedClubId] = useState(clubs[0]?.id || "");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(90);

  useEffect(() => {
    if (!selectedClubId && clubs[0]) setSelectedClubId(clubs[0].id);
  }, [clubs, selectedClubId]);

  const load = useCallback(async () => {
    if (!selectedClubId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/clubs/${selectedClubId}/upcoming-events?days=${days}&limit=200`);
      setEvents(data.events || []);
    } catch {  // eslint-disable-line no-empty -- silent on transient errors
    } finally { setLoading(false); }
  }, [selectedClubId, days]);

  useEffect(() => { load(); }, [load]);

  // Group by day for a clean timeline
  const byDay = {};
  for (const e of events) {
    const day = (e.start || "").slice(0, 10) || "TBA";
    if (!byDay[day]) byDay[day] = [];
    byDay[day].push(e);
  }
  const orderedDays = Object.keys(byDay).sort();

  if (clubs.length === 0) return null;

  return (
    <div className="pp-card-elevated p-6 space-y-4" data-testid="club-event-calendar">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Calendar size={18} className="text-[#007AFF]" />
          <h3 className="font-heading text-lg font-bold">Club event calendar</h3>
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 text-zinc-400">
            {loading ? "…" : `${events.length} upcoming`}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {clubs.length > 1 && (
            <select
              value={selectedClubId}
              onChange={(e) => setSelectedClubId(e.target.value)}
              className="pp-input text-xs"
              data-testid="cec-club-select"
            >
              {clubs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="cec-horizon"
            className="pp-input text-xs"
          >
            <option value={30}>Next 30 days</option>
            <option value={90}>Next 90 days</option>
            <option value={180}>Next 6 months</option>
            <option value={365}>Next 12 months</option>
          </select>
        </div>
      </div>

      {!loading && events.length === 0 && (
        <div className="text-center py-8 text-zinc-500 text-sm" data-testid="cec-empty">
          <Calendar size={28} className="mx-auto mb-2 text-zinc-700" />
          No upcoming events for this club. Once you create matches, tournaments, leagues or lessons,
          they'll appear here.
        </div>
      )}

      {orderedDays.map((day) => (
        <div key={day} className="space-y-2" data-testid={`cec-day-${day}`}>
          <div className="text-xs uppercase tracking-wider text-zinc-500 sticky top-0 bg-zinc-950/80 backdrop-blur-sm py-1 z-10">
            {_fmtDay(day)}
          </div>
          {byDay[day].map((e) => {
            const meta = KIND_META[e.kind] || KIND_META.match;
            const Icon = meta.icon;
            return (
              <div
                key={`${e.kind}-${e.id}`}
                data-testid={`cec-event-${e.kind}-${e.id}`}
                className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] hover:bg-white/[0.05] border border-white/5 transition-colors"
              >
                <div className={`shrink-0 h-9 w-9 rounded-lg ${meta.bg} flex items-center justify-center`}>
                  <Icon size={16} className={meta.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <Link
                    to={`/${meta.route}/${e.id}`}
                    className="font-medium hover:text-[#00E676] transition-colors flex items-center gap-1 truncate"
                  >
                    {e.title} <ChevronRight size={14} className="opacity-50" />
                  </Link>
                  <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${meta.bg} ${meta.color} uppercase`}>
                      {meta.label}
                    </span>
                    {_fmtTime(e.start) && <span>{_fmtTime(e.start)}</span>}
                    {e.facility && (
                      <Link to={`/facilities/${e.facility.slug || e.facility.id}`} className="flex items-center gap-1 hover:text-zinc-300">
                        <MapPin size={10} /> {e.facility.name}
                      </Link>
                    )}
                  </div>
                </div>
                <div className="shrink-0">
                  <AddToCalendarButton
                    kind={e.kind}
                    id={e.id}
                    start={e.start}
                    end={e.end}
                    title={e.title}
                    location={e.facility?.name || ""}
                    size="sm"
                    testIdSuffix={`cec-${e.kind}-${e.id}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
